import {
  CalendarOutlined,
  CheckCircleOutlined,
  EditOutlined,
  EllipsisOutlined,
  EyeOutlined,
  FilterOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
} from '@ant-design/icons';
import type { MenuProps, TableProps } from 'antd';
import {
  Button,
  DatePicker,
  Dropdown,
  Input,
  Radio,
  Rate,
  Select,
  Switch,
  Table,
} from 'antd';
import type { ColumnsType, TableRowSelection } from 'antd/es/table/interface';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { PreviewImage } from '@/components/PreviewImage/PreviewImage';
import { ROUTES } from '@/constants/routes';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import { useLazyGetBrandsQuery } from '@/modules/brands/brandsApi';
import { useLazyGetCategoriesQuery } from '@/modules/categories/categoriesApi';
import { useGetAttributesQuery } from '@/modules/attributes/attributesApi';
import { useLazyGetAttributeValuesQuery } from '@/modules/attribute-values/attributeValuesApi';
import {
  useBulkDeleteProductsMutation,
  useBulkUpdateProductStatusMutation,
  useDeleteProductMutation,
  useGetProductsQuery,
  useImportProductsMutation,
  useUpdateProductMutation,
} from '@/modules/products/productsApi';
import { useAppSelector } from '@/store/hooks';
import type { Product, ProductImportResult, ProductStatusFilter } from '@/types/product';
import { isImprintAttribute } from '@/utils/attribute';
import { resolveMutationResult } from '@/utils/apiResponse';
import {
  confirmPermanentProductBulkDelete,
  confirmPermanentProductDelete,
} from '@/utils/confirmDialog';
import { downloadAuthenticatedFile } from '@/utils/downloadFile';
import { authenticatedFetch } from '@/utils/authSession';
import { isApiResponse } from '@/types/api';
import { formatDateTime } from '@/utils/date';
import { getMediaImageUrl } from '@/utils/media';
import {
  getProductListStock,
} from '@/utils/product';
import { ucfirst } from '@/utils/string';
import { ColumnFilterPanel } from '../categories/components/ColumnFilterPanel';
import { SortableColumnTitle } from '../categories/components/SortableColumnTitle';
import { useProductQueryParams } from './hooks/useProductQueryParams';
import { BulkUploadDropdown } from './components/BulkUploadDropdown';
import { ProductImportResultModal } from './components/ProductImportResultModal';
import styles from './Products.module.scss';

export default function Products() {
  const navigate = useNavigate();
  const notify = useNotify();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { state, apiParams, patchParams, resetAll, hasActiveFiltersOrSort } =
    useProductQueryParams();
  const { data: listResult, isLoading, isFetching, refetch } = useGetProductsQuery(apiParams);
  const accessToken = useAppSelector((store) => store.auth.accessToken);
  useGlobalLoader(isFetching && !!listResult);

  const products = useMemo(() => listResult?.results ?? [], [listResult?.results]);
  const total = listResult?.total ?? 0;
  const currentPage = Number(listResult?.page ?? state.page);
  const pageSize = Number(listResult?.limit ?? state.limit);

  const [searchDraft, setSearchDraft] = useState(state.search);
  const [statusDraft, setStatusDraft] = useState<ProductStatusFilter>(state.status);
  const [brandDraft, setBrandDraft] = useState<string[]>(state.brandIds);
  const [categoryDraft, setCategoryDraft] = useState<string[]>(state.categoryIds);
  const [imprintDraft, setImprintDraft] = useState<string[]>(state.imprintValueIds);
  const [dateDraft, setDateDraft] = useState<[Dayjs | null, Dayjs | null] | null>(() =>
    state.dateFrom || state.dateTo
      ? [state.dateFrom ? dayjs(state.dateFrom) : null, state.dateTo ? dayjs(state.dateTo) : null]
      : null,
  );
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [isExporting, setIsExporting] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [importResult, setImportResult] = useState<ProductImportResult | null>(null);
  const [importMessage, setImportMessage] = useState<string | null>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);

  const [fetchBrands, { data: brandsResult, isFetching: brandsLoading }] = useLazyGetBrandsQuery();
  const [fetchCategories, { data: categoriesResult, isFetching: categoriesLoading }] =
    useLazyGetCategoriesQuery();
  const { data: attributesResult } = useGetAttributesQuery({
    page: 1,
    limit: 100,
    sortBy: 'sortOrder',
    sortOrder: 'asc',
    status: 'active',
  });
  const [fetchImprints, { data: imprintsResult, isFetching: imprintsLoading }] =
    useLazyGetAttributeValuesQuery();

  const [updateProduct] = useUpdateProductMutation();
  const [deleteProduct] = useDeleteProductMutation();
  const [bulkUpdateStatus, { isLoading: isBulkUpdating }] = useBulkUpdateProductStatusMutation();
  const [bulkDelete, { isLoading: isBulkDeleting }] = useBulkDeleteProductsMutation();
  const [importProducts, { isLoading: isImporting }] = useImportProductsMutation();

  const clearSelection = () => setSelectedRowKeys([]);

  const imprintAttributeId = useMemo(
    () => (attributesResult?.results ?? []).find((attribute) => isImprintAttribute(attribute))?.id,
    [attributesResult?.results],
  );

  const brandFilterOptions = useMemo(() => {
    const brands = brandsResult?.results ?? [];
    const options = brands.map((brand) => ({ label: ucfirst(brand.name), value: brand.id }));

    state.brandIds.forEach((brandId) => {
      if (!options.some((option) => option.value === brandId)) {
        const name =
          products.find((item) => item.brandIds.includes(brandId))?.brandNames?.[0] ?? brandId;
        options.unshift({ label: ucfirst(name), value: brandId });
      }
    });

    return options;
  }, [brandsResult?.results, products, state.brandIds]);

  const categoryFilterOptions = useMemo(() => {
    const categories = categoriesResult?.results ?? [];
    const options = categories.map((category) => ({
      label: ucfirst(category.name),
      value: category.id,
    }));

    state.categoryIds.forEach((categoryId) => {
      if (!options.some((option) => option.value === categoryId)) {
        const name =
          products.find((item) => item.categoryIds.includes(categoryId))?.categoryNames?.[0] ??
          categoryId;
        options.unshift({ label: ucfirst(name), value: categoryId });
      }
    });

    return options;
  }, [categoriesResult?.results, products, state.categoryIds]);

  const imprintFilterOptions = useMemo(() => {
    const imprints = imprintsResult?.results ?? [];
    const options = imprints.map((imprint) => ({
      label: ucfirst(imprint.name),
      value: imprint.id,
    }));

    state.imprintValueIds.forEach((imprintValueId) => {
      if (!options.some((option) => option.value === imprintValueId)) {
        const name =
          products.find((item) => item.imprintValueId === imprintValueId)?.imprint ?? imprintValueId;
        options.unshift({ label: ucfirst(name), value: imprintValueId });
      }
    });

    return options;
  }, [imprintsResult?.results, products, state.imprintValueIds]);

  const handleResetFiltersAndSort = () => {
    setSearchDraft('');
    setStatusDraft('all');
    setBrandDraft([]);
    setCategoryDraft([]);
    setImprintDraft([]);
    setDateDraft(null);
    resetAll();
  };

  const handleSort = useCallback(
    (field: string) => {
      const nextOrder: 'asc' | 'desc' =
        state.sortBy === field ? (state.sortOrder === 'asc' ? 'desc' : 'asc') : 'asc';
      patchParams({ sortBy: field, sortOrder: nextOrder, page: 1 });
    },
    [patchParams, state.sortBy, state.sortOrder],
  );

  const handleStatusChange = useCallback(
    async (product: Product, isActive: boolean) => {
      const result = await resolveMutationResult(
        updateProduct({ id: product.id, body: { isActive } }),
      );
      if (result.ok) {
        notify.success(result.message ?? 'Status updated.');
        return;
      }
      notify.error(result.message);
    },
    [notify, updateProduct],
  );

  const handleDelete = useCallback(
    async (product: Product) => {
      const confirmed = await confirmPermanentProductDelete(product.name);
      if (!confirmed) return;

      const result = await resolveMutationResult(deleteProduct(product.id));
      if (result.ok) {
        notify.success(result.message ?? 'Product deleted.');
        setSelectedRowKeys((keys) => keys.filter((key) => key !== product.id));
        return;
      }
      notify.error(result.message);
    },
    [deleteProduct, notify],
  );

  const handleBulkStatus = async (isActive: boolean) => {
    if (!selectedRowKeys.length) return;
    const result = await resolveMutationResult(
      bulkUpdateStatus({ ids: selectedRowKeys, isActive }),
    );
    if (result.ok) {
      notify.success(result.message ?? 'Products updated.');
      clearSelection();
      return;
    }
    notify.error(result.message);
  };

  const handleBulkDelete = async () => {
    if (!selectedRowKeys.length) return;
    const confirmed = await confirmPermanentProductBulkDelete(selectedRowKeys.length);
    if (!confirmed) return;

    const result = await resolveMutationResult(bulkDelete({ ids: selectedRowKeys }));
    if (result.ok) {
      notify.success(result.message ?? 'Products deleted.');
      clearSelection();
    } else {
      notify.error(result.message);
    }
  };

  const getRowActionItems = useCallback(
    (product: Product): MenuProps['items'] => [
      {
        key: 'view',
        label: 'View',
        icon: <EyeOutlined />,
        onClick: () => navigate(`/products/${product.id}/view`),
      },
      {
        key: 'edit',
        label: 'Edit',
        icon: <EditOutlined />,
        onClick: () => navigate(`/products/${product.id}/edit`),
      },
      { type: 'divider' },
      { key: 'delete', label: 'Delete', danger: true, onClick: () => handleDelete(product) },
    ],
    [handleDelete, navigate],
  );

  const columns: ColumnsType<Product> = useMemo(
    () => [
      {
        title: (
          <SortableColumnTitle
            label="NAME"
            field="name"
            activeField={state.sortBy}
            sortOrder={state.sortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'name',
        key: 'name',
        className: styles.nameCol,
        filteredValue: state.search ? [state.search] : null,
        filterIcon: (filtered) => (
          <SearchOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel showActions={false}>
            <Input
              allowClear
              size="small"
              prefix={<SearchOutlined />}
              placeholder="Search name, slug, SKU"
              value={searchDraft}
              onChange={(e) => {
                const value = e.target.value;
                setSearchDraft(value);
                if (!value) {
                  patchParams({ search: null }, { resetPage: true });
                }
              }}
              onPressEnter={() => {
                patchParams({ search: searchDraft.trim() || null }, { resetPage: true });
                close();
              }}
            />
          </ColumnFilterPanel>
        ),
        render: (name: string, record) => (
          <Link to={`/products/${record.id}/view`} className={styles.nameLink}>
            <div className={styles.nameRow}>
              <PreviewImage
                src={getMediaImageUrl(record.thumbnail?.imageLarge ?? record.thumbnail?.imageSmall)}
                size={36}
                className={styles.thumb}
                fallback={ucfirst(name).charAt(0)}
                alt={name}
              />
              <div className={styles.nameBlock}>
                <span className={styles.nameCell}>{ucfirst(name)}</span>
                {record.reviewCount ? (
                  <Rate
                    disabled
                    allowHalf
                    value={record.averageRating ?? 0}
                    className={styles.ratingStars}
                  />
                ) : null}
                <span className={styles.slug}>{record.sku}</span>
              </div>
            </div>
          </Link>
        ),
      },
      {
        title: 'BRAND',
        key: 'brand',
        className: styles.brandCol,
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.brandIds.length ? state.brandIds : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel showActions={false}>
            <Select
              allowClear
              showSearch
              size="small"
              className={styles.filterSelect}
              placeholder="All brands"
              optionFilterProp="label"
              options={brandFilterOptions}
              loading={brandsLoading}
              value={brandDraft[0]}
              onChange={(value?: string) => {
                const next = value ? [value] : [];
                setBrandDraft(next);
                patchParams({ brandIds: next.length ? next : null }, { resetPage: true });
                close();
              }}
              onOpenChange={(visible) => {
                if (visible) {
                  void fetchBrands({
                    page: 1,
                    limit: 100,
                    sortBy: 'name',
                    sortOrder: 'asc',
                  });
                }
              }}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (visible) => {
            if (visible) setBrandDraft(state.brandIds);
          },
        },
        render: (_v, record) => (
          <span className={styles.metaCell}>
            {record.brandNames?.length ? record.brandNames.map(ucfirst).join(', ') : '—'}
          </span>
        ),
      },
      {
        title: 'CATEGORY',
        key: 'category',
        className: styles.categoryCol,
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.categoryIds.length ? state.categoryIds : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel showActions={false}>
            <Select
              allowClear
              showSearch
              size="small"
              className={styles.filterSelect}
              placeholder="All categories"
              optionFilterProp="label"
              options={categoryFilterOptions}
              loading={categoriesLoading}
              value={categoryDraft[0]}
              onChange={(value?: string) => {
                const next = value ? [value] : [];
                setCategoryDraft(next);
                patchParams({ categoryIds: next.length ? next : null }, { resetPage: true });
                close();
              }}
              onOpenChange={(visible) => {
                if (visible) {
                  void fetchCategories({
                    page: 1,
                    limit: 100,
                    sortBy: 'name',
                    sortOrder: 'asc',
                  });
                }
              }}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (visible) => {
            if (visible) setCategoryDraft(state.categoryIds);
          },
        },
        render: (_v, record) => (
          <span className={styles.metaCell}>
            {record.categoryNames?.length ? record.categoryNames.map(ucfirst).join(', ') : '—'}
          </span>
        ),
      },
      {
        title: 'IMPRINT',
        key: 'imprint',
        dataIndex: 'imprint',
        ellipsis: true,
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.imprintValueIds.length ? state.imprintValueIds : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel showActions={false}>
            <Select
              allowClear
              showSearch
              size="small"
              className={styles.filterSelect}
              placeholder="All imprints"
              optionFilterProp="label"
              options={imprintFilterOptions}
              loading={imprintsLoading}
              value={imprintDraft[0]}
              onChange={(value?: string) => {
                const next = value ? [value] : [];
                setImprintDraft(next);
                patchParams(
                  { imprintValueIds: next.length ? next : null },
                  { resetPage: true },
                );
                close();
              }}
              onOpenChange={(visible) => {
                if (visible && imprintAttributeId) {
                  void fetchImprints({
                    attributeId: imprintAttributeId,
                    page: 1,
                    limit: 100,
                    sortBy: 'name',
                    sortOrder: 'asc',
                    status: 'active',
                  });
                }
              }}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (visible) => {
            if (visible) setImprintDraft(state.imprintValueIds);
          },
        },
        render: (imprint: string | undefined, record) => (
          <span className={styles.imprintCell}>
            {imprint?.trim() || record.seriesName?.trim() || '—'}
          </span>
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="STOCK"
            field="stock"
            activeField={state.sortBy}
            sortOrder={state.sortOrder}
            onSort={handleSort}
          />
        ),
        key: 'stock',
        align: 'center',
        render: (_v, record) => getProductListStock(record),
      },
      {
        title: (
          <SortableColumnTitle
            label="STATUS"
            field="isActive"
            activeField={state.sortBy}
            sortOrder={state.sortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'isActive',
        key: 'isActive',
        align: 'center',
        filteredValue: state.status !== 'all' ? [state.status] : null,
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel showActions={false}>
            <Radio.Group
              value={statusDraft}
              onChange={(e) => {
                const value = e.target.value as ProductStatusFilter;
                setStatusDraft(value);
                patchParams(
                  { status: value === 'all' ? null : value },
                  { resetPage: true },
                );
                close();
              }}
            >
              <Radio value="all">All</Radio>
              <Radio value="active">Active</Radio>
              <Radio value="inactive">Inactive</Radio>
            </Radio.Group>
          </ColumnFilterPanel>
        ),
        render: (isActive: boolean, record) => (
          <Switch
            checked={isActive}
            size="small"
            onChange={(checked) => handleStatusChange(record, checked)}
          />
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="UPDATED"
            field="updatedAt"
            activeField={state.sortBy}
            sortOrder={state.sortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        filterIcon: (filtered) => (
          <CalendarOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.dateFrom || state.dateTo ? ['filtered'] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel showActions={false}>
            <DatePicker.RangePicker
              size="small"
              value={dateDraft}
              onChange={(values: [Dayjs | null, Dayjs | null] | null) => {
                setDateDraft(values);
                patchParams(
                  {
                    dateFrom: values?.[0]?.format('YYYY-MM-DD') || null,
                    dateTo: values?.[1]?.format('YYYY-MM-DD') || null,
                  },
                  { resetPage: true },
                );
                if (!values || (values[0] && values[1])) close();
              }}
              style={{ width: '100%' }}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) {
              setDateDraft(
                state.dateFrom || state.dateTo
                  ? [
                      state.dateFrom ? dayjs(state.dateFrom) : null,
                      state.dateTo ? dayjs(state.dateTo) : null,
                    ]
                  : null,
              );
            }
          },
        },
        render: (value: string) => <span className={styles.dateCell}>{formatDateTime(value)}</span>,
      },
      {
        title: 'ACTION',
        key: 'actions',
        align: 'center',
        render: (_v, record) => (
          <Dropdown menu={{ items: getRowActionItems(record) }} trigger={['click']}>
            <Button
              type="text"
              size="small"
              icon={<EllipsisOutlined />}
              className={styles.actionBtn}
            />
          </Dropdown>
        ),
      },
    ],
    [
      brandDraft,
      brandFilterOptions,
      brandsLoading,
      categoryDraft,
      categoryFilterOptions,
      categoriesLoading,
      dateDraft,
      fetchBrands,
      fetchCategories,
      fetchImprints,
      getRowActionItems,
      handleSort,
      handleStatusChange,
      imprintAttributeId,
      imprintDraft,
      imprintFilterOptions,
      imprintsLoading,
      patchParams,
      searchDraft,
      state.brandIds,
      state.categoryIds,
      state.dateFrom,
      state.dateTo,
      state.imprintValueIds,
      state.search,
      state.sortBy,
      state.sortOrder,
      state.status,
      statusDraft,
    ],
  );

  const rowSelection: TableRowSelection<Product> = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys as string[]),
  };

  const handleDownloadTemplate = useCallback(async () => {
    setIsDownloadingTemplate(true);
    try {
      const filename = await downloadAuthenticatedFile(
        '/products/export-template',
        accessToken,
        'products-template.xlsx',
      );
      notify.success(`Downloaded ${filename}`);
    } catch {
      notify.error('Could not download the product template.');
    } finally {
      setIsDownloadingTemplate(false);
    }
  }, [accessToken, notify]);

  const handleExportProducts = useCallback(async () => {
    setIsExporting(true);
    try {
      const startResponse = await authenticatedFetch('/products/export/jobs', {
        method: 'POST',
      });
      if (!startResponse.ok) {
        throw new Error('Could not start product export.');
      }

      const startPayload: unknown = await startResponse.json();
      const startData = isApiResponse<{ jobId?: string }>(startPayload)
        ? startPayload.data
        : (startPayload as { jobId?: string });
      const jobId = startData?.jobId?.trim();
      if (!jobId) {
        throw new Error('Export job id was missing.');
      }

      const maxAttempts = 600;
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const statusResponse = await authenticatedFetch(`/products/export/jobs/${jobId}`);
        if (!statusResponse.ok) {
          throw new Error('Could not check export progress.');
        }

        const statusPayload: unknown = await statusResponse.json();
        const statusData = isApiResponse<{
          status?: string;
          errorMessage?: string;
          progress?: number;
        }>(statusPayload)
          ? statusPayload.data
          : (statusPayload as {
              status?: string;
              errorMessage?: string;
              progress?: number;
            });

        if (statusData?.status === 'ready') {
          const filename = await downloadAuthenticatedFile(
            `/products/export/jobs/${jobId}/file`,
            accessToken,
            'products.xlsx',
          );
          notify.success(`Exported ${filename}`);
          return;
        }

        if (statusData?.status === 'failed') {
          throw new Error(statusData.errorMessage || 'Product export failed.');
        }

        await new Promise((resolve) => {
          window.setTimeout(resolve, 1500);
        });
      }

      throw new Error('Product export timed out. Please try again.');
    } catch (error) {
      notify.error(error instanceof Error ? error.message : 'Could not export products.');
    } finally {
      setIsExporting(false);
    }
  }, [accessToken, notify]);

  const handleImportFile = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;

      const result = await resolveMutationResult<ProductImportResult>(importProducts(file));
      if (!result.ok) {
        notify.error(result.message);
        return;
      }

      setImportResult(result.data);
      setImportMessage(result.message ?? null);
      setImportModalOpen(true);
      notify.success(result.message ?? 'Product import finished.');
      void refetch();
    },
    [importProducts, notify, refetch],
  );

  const handleTableChange: TableProps<Product>['onChange'] = (pagination) => {
    patchParams({
      page: pagination.current ?? 1,
      limit: pagination.pageSize ?? pageSize,
    });
  };

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          <>
            {hasActiveFiltersOrSort ? (
              <Button icon={<ReloadOutlined />} onClick={handleResetFiltersAndSort}>
                Reset
              </Button>
            ) : null}
            <BulkUploadDropdown
              onDownloadTemplate={() => void handleDownloadTemplate()}
              onExportProducts={() => void handleExportProducts()}
              onImport={() => fileInputRef.current?.click()}
              isDownloadingTemplate={isDownloadingTemplate}
              isExporting={isExporting}
              isImporting={isImporting}
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate(ROUTES.PRODUCT_CREATE)}
            >
              Add product
            </Button>
          </>
        }
      />

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls"
        hidden
        onChange={(event) => void handleImportFile(event)}
      />

      <ProductImportResultModal
        open={importModalOpen}
        result={importResult}
        message={importMessage}
        onClose={() => setImportModalOpen(false)}
      />

      {selectedRowKeys.length > 0 ? (
        <div className={styles.bulkBar}>
          <span>{selectedRowKeys.length} selected</span>
          <Button
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() => handleBulkStatus(true)}
            loading={isBulkUpdating}
          >
            Activate
          </Button>
          <Button
            size="small"
            icon={<StopOutlined />}
            onClick={() => handleBulkStatus(false)}
            loading={isBulkUpdating}
          >
            Deactivate
          </Button>
          <Button size="small" danger onClick={handleBulkDelete} loading={isBulkDeleting}>
            Delete
          </Button>
          <Button size="small" type="text" onClick={clearSelection}>
            Clear
          </Button>
        </div>
      ) : null}

      <div className={styles.tableCard}>
        <Table<Product>
          className={styles.table}
          rowKey="id"
          size="small"
          scroll={{ x: 'max-content' }}
          loading={isLoading && !listResult}
          columns={columns}
          dataSource={products}
          rowSelection={rowSelection}
          onChange={handleTableChange}
          pagination={{
            current: currentPage,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50],
            showTotal: (totalCount, [start, end]) => `${start}-${end} of ${totalCount}`,
            size: 'small',
          }}
        />
      </div>
    </div>
  );
}
